import { useState, useRef, useEffect } from "react";
import { ChevronDown, Loader2 } from "@semo/icons";
import { Input } from "@semo/shared-ui";
import { useSupplierItemAutocomplete } from "../../features/quotes/presentation/hooks/useSupplierItemAutocomplete";

interface ItemNameAutocompleteProps {
  value: string;
  supplierId: string;
  onSelect: (
    itemName: string,
    unitPrice: number,
    itemId: string,
    spec1?: string | null,
    spec2?: string | null,
    spec3?: string | null,
  ) => void;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputSize?: React.ComponentProps<typeof Input>["inputSize"];
}

/**
 * 아이템 이름 자동완성 컴포넌트
 *
 * 판매자 대시보드 견적 참여 시 사용
 * - 공급사의 과거 거래 내역 기반 단가 자동 제공
 * - 최소 2글자 이상 입력 시 자동완성 드롭다운 표시
 * - 항목 선택 시 품명 + 단가 자동 입력
 */
export function ItemNameAutocomplete({
  value,
  supplierId,
  onSelect,
  onChange,
  placeholder = "품명 입력",
  className = "",
  inputSize = "default",
}: ItemNameAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { results, isLoading, search, clear } =
    useSupplierItemAutocomplete(supplierId);

  // Input change handler
  const handleInputChange = (newValue: string) => {
    onChange(newValue);
    search(newValue);

    if (newValue.length >= 2) {
      setIsOpen(true);
      setSelectedIndex(-1);
    } else {
      setIsOpen(false);
    }
  };

  // Item selection handler
  const handleSelectItem = (
    itemName: string,
    unitPrice: number,
    itemId: string,
    spec1?: string | null,
    spec2?: string | null,
    spec3?: string | null,
  ) => {
    onSelect(itemName, unitPrice, itemId, spec1, spec2, spec3);
    setIsOpen(false);
    clear();
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          const selectedItem = results[selectedIndex];
          if (selectedItem) {
            const unitPrice = selectedItem.supplierPrice || selectedItem.price;
            handleSelectItem(
              selectedItem.name,
              unitPrice,
              selectedItem.id,
              selectedItem.spec1,
              selectedItem.spec2,
              selectedItem.spec3,
            );
          }
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (value.length >= 2) {
              setIsOpen(true);
            }
          }}
          className={`w-full text-sm font-semibold text-gray-900 ${className}`}
          inputSize={inputSize}
          placeholder={placeholder}
          autoComplete="off"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          </div>
        )}
        {!isLoading && results.length > 0 && isOpen && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </div>
        )}
      </div>

      {/* Autocomplete Dropdown */}
      {isOpen && results.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {results.map((item, index) => {
            const unitPrice = item.supplierPrice || item.price;
            const hasSupplierPrice = Boolean(item.supplierPrice);

            return (
              <div
                key={item.id}
                className={`px-3 py-2 cursor-pointer transition-colors ${
                  index === selectedIndex
                    ? "bg-primary/5 border-l-2 border-primary"
                    : "hover:bg-gray-50"
                }`}
                onClick={() =>
                  handleSelectItem(
                    item.name,
                    unitPrice,
                    item.id,
                    item.spec1,
                    item.spec2,
                    item.spec3,
                  )
                }
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {item.name}
                    </div>
                    {item.categoryPath && (
                      <div className="text-xs text-gray-500 truncate mt-0.5">
                        {item.categoryPath}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    {hasSupplierPrice ? (
                      <div className="text-sm font-semibold text-primary-text">
                        {unitPrice.toLocaleString("ko-KR")}원
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">
                        {unitPrice.toLocaleString("ko-KR")}원
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-0.5">
                      {hasSupplierPrice ? "거래 단가" : "기준 가격"}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* No results message */}
      {isOpen && !isLoading && results.length === 0 && value.length >= 2 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-4 text-center"
        >
          <div className="text-sm text-gray-500">검색 결과가 없습니다</div>
          <div className="text-xs text-gray-400 mt-1">
            다른 키워드로 검색해보세요
          </div>
        </div>
      )}
    </div>
  );
}
